import axios, { AxiosError, AxiosResponse } from 'axios';
import { ApiError } from '../types';

/**
 * API Client Configuration
 * CRITICAL: No Authorization headers - uses cookies only
 * Validates: Requirements 1.1, 23.1, 23.2, 23.3
 */

// Helper to get cookie value
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null;
  }
  return null;
}

// Create axios instance with correct configuration
// Validates: Requirements 1.1, 23.1, 23.2
export const apiClient = axios.create({
  baseURL: '/bff', // Same-origin via Vite proxy
  withCredentials: true, // Include cookies - CRITICAL for cookie-based auth
  headers: {
    'Content-Type': 'application/json',
    // NO Authorization header - CRITICAL: tokens are handled by BFF
  },
  timeout: 30000, // 30 second timeout
});

// CSRF Token Interceptor
// Validates: Requirements 3.3, 23.3
apiClient.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase();

  // Debug logging
  console.log('[API Request]', {
    method: config.method,
    url: config.url,
    baseURL: config.baseURL,
    fullURL: `${config.baseURL}${config.url}`,
  });

  // Add CSRF token for state-changing requests
  if (method && ['post', 'put', 'delete', 'patch'].includes(method)) {
    const csrfToken = getCookie('__Host-csrf');
    console.log('[CSRF Token]', csrfToken ? 'Present' : 'Missing');
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
}, (error) => {
  console.error('[API Request Error]', error);
  return Promise.reject(error);
});

// Debounce guard for 401 handling
// Validates: Requirements 10.1
let isHandling401 = false;
let handling401Timeout: ReturnType<typeof setTimeout> | null = null;

// Autosave flush callback - can be set by components that need to save before logout
let autosaveFlushCallback: (() => Promise<void>) | null = null;

/**
 * Register a callback to flush autosave data before logout
 * Validates: Requirements 10.2
 */
export function registerAutosaveFlush(callback: () => Promise<void>): void {
  autosaveFlushCallback = callback;
}

/**
 * Unregister the autosave flush callback
 */
export function unregisterAutosaveFlush(): void {
  autosaveFlushCallback = null;
}

/**
 * Broadcast auth event to other tabs
 * Validates: Requirements 10.3
 */
function broadcastAuthEvent(type: 'logout' | 'session-expired', reason?: string): void {
  try {
    const authChannel = new BroadcastChannel('auth');
    authChannel.postMessage({ type, reason });
    authChannel.close();
  } catch {
    // BroadcastChannel not supported, use localStorage fallback
    localStorage.setItem('auth-event', JSON.stringify({ 
      type, 
      reason,
      timestamp: Date.now() 
    }));
  }
}

/**
 * Show toast notification for auth errors
 * Validates: Requirements 10.4
 */
function showAuthErrorToast(message: string): void {
  // Dispatch custom event for toast notification
  // Components can listen to this event to show localized messages
  window.dispatchEvent(new CustomEvent('auth-error', { 
    detail: { message } 
  }));
}

// Response interceptor for auth errors
// Validates: Requirements 10.1, 10.2, 10.3, 10.4, 23.4
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log('[API Response]', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    console.error('[API Error]', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
      data: error.response?.data,
    });
    const status = error.response?.status;
    const errorData = error.response?.data;
    const requestUrl = error.config?.url || '';

    // Handle 401 Unauthorized with debounce guard
    // Skip redirect if we're checking session (expected 401 for unauthenticated users)
    const isSessionCheck = requestUrl.includes('/auth/session');
    const isAlreadyOnLoginPage = window.location.pathname === '/login';

    if (status === 401 && !isHandling401 && !isSessionCheck && !isAlreadyOnLoginPage) {
      isHandling401 = true;
      
      // Clear any existing timeout
      if (handling401Timeout) {
        clearTimeout(handling401Timeout);
      }
      
      try {
        // Flush autosave data before logout (Requirement 10.2)
        if (autosaveFlushCallback) {
          try {
            await autosaveFlushCallback();
          } catch (flushError) {
            console.error('Failed to flush autosave data:', flushError);
          }
        }
        
        // Broadcast to other tabs (Requirement 10.3)
        const reason = errorData?.error || 'SESSION_EXPIRED';
        broadcastAuthEvent('session-expired', reason);
        
        // Show localized toast message (Requirement 10.4)
        const message = getLocalizedAuthErrorMessage(reason);
        showAuthErrorToast(message);
        
        // Redirect to login (Requirement 23.4)
        // Small delay to allow toast to show
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
        
      } finally {
        // Reset debounce guard after delay
        handling401Timeout = setTimeout(() => {
          isHandling401 = false;
        }, 2000);
      }
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get localized error message for auth errors
 * Validates: Requirements 10.4
 */
function getLocalizedAuthErrorMessage(errorCode: string): string {
  const messages: Record<string, string> = {
    'SESSION_EXPIRED': 'Your session has expired. Please log in again.',
    'SESSION_INVALID': 'Your session is invalid. Please log in again.',
    'CSRF_INVALID': 'Security validation failed. Please refresh the page.',
    'RATE_LIMITED': 'Too many requests. Please wait a moment.',
    'AUTH_FAILED': 'Authentication failed. Please try again.',
    'OTHER_DEVICE_LOGIN': 'You have been logged out because you logged in on another device.',
    'ADMIN_REVOKE': 'Your session was revoked by an administrator.',
  };
  
  return messages[errorCode] || 'An authentication error occurred. Please log in again.';
}

/**
 * API response type helper
 */
export type ApiResponse<T> = {
  data: T;
  status: number;
};

export default apiClient;
